import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import * as Dialog from "@radix-ui/react-dialog";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { saveOrderAsTemplate } from "@/api/orderTemplates";
import { applyApiErrorToForm } from "@/lib/apiErrors";
import { toastSuccess } from "@/lib/toast";
import type { Order } from "@/types/orders";

const schema = z.object({
  name: z.string().min(1, "Required"),
});

type FormValues = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: Order;
}

const inputCls =
  "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary";
const labelCls = "mb-1 block text-xs font-semibold text-gray-600";

export function SaveAsTemplateModal({ open, onOpenChange, order }: Props) {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: order.address },
  });

  // Pre-fill with the order address each time the modal opens
  useEffect(() => {
    if (open) {
      reset({ name: order.address });
    }
  }, [open, order.address, reset]);

  const mutation = useMutation({
    mutationFn: ({ name }: FormValues) => saveOrderAsTemplate(order.id, name),
    onSuccess: () => {
      toastSuccess(t("orderTemplates.toast.saved"));
      onOpenChange(false);
    },
    onError: (err: unknown) => {
      // BUG-464: a duplicate template name comes back as a per-field `name` error; it now
      // renders under the name input instead of the axios transport string.
      applyApiErrorToForm(err, t, setError, { name: "name" });
    },
  });

  function onSubmit(values: FormValues) {
    mutation.mutate(values);
  }

  function handleClose() {
    onOpenChange(false);
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl bg-white shadow-xl focus:outline-none">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
            <Dialog.Title className="text-base font-bold text-gray-900">
              {t("orderTemplates.modal.saveAsTemplateTitle")}
            </Dialog.Title>
            <Dialog.Close
              onClick={handleClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            >
              <X size={16} />
            </Dialog.Close>
          </div>

          {/* Body */}
          <div className="p-6">
            {errors.root && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {errors.root.message}
              </div>
            )}

            <form id="save-as-template-form" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <label className={labelCls}>
                  {t("orderTemplates.modal.templateName")}{" "}
                  <span className="text-red-500">*</span>
                </label>
                <input {...register("name")} className={inputCls} />
                {errors.name && (
                  <p className="mt-1 text-xs text-red-600">
                    {errors.name.message}
                  </p>
                )}
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-4">
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
            >
              {t("orderTemplates.modal.cancel")}
            </button>
            <button
              type="submit"
              form="save-as-template-form"
              disabled={mutation.isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-brand-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
            >
              {mutation.isPending && (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              )}
              {t("orderTemplates.modal.saveTemplate")}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
